// Briefversand per E-POSTBUSINESS API (Edge epost-brief) + Schalter app_settings 'epost_versand'.
import { supabase } from '../supabase/client';
import type { Absender, BriefOptionen, Empfaenger } from '../../supabase/functions/epost-brief/logik';

export type { Absender, BriefOptionen, Empfaenger };
export { pruefeEmpfaenger, statusText, EINSCHREIBEN } from '../../supabase/functions/epost-brief/logik';

export const SCHALTER_EPOST = 'epost_versand';

export interface EpostEinstellung { aktiv: boolean; modus: 'test' | 'live'; status_abgleich: boolean; test_email: string; absender: Absender }

const LEER: EpostEinstellung = { aktiv: false, modus: 'test', status_abgleich: true, test_email: '', absender: { name: '', strasse: '', plz: '', ort: '' } };

export async function ladeEpostEinstellung(): Promise<EpostEinstellung> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', SCHALTER_EPOST).maybeSingle();
  if (error) throw error;
  const v = (data?.value ?? {}) as Partial<EpostEinstellung>;
  return { aktiv: v.aktiv === true, modus: v.modus === 'live' ? 'live' : 'test', status_abgleich: v.status_abgleich !== false, test_email: v.test_email ?? '', absender: { ...LEER.absender, ...(v.absender ?? {}) } };
}

export async function speichereEpostEinstellung(e: EpostEinstellung): Promise<void> {
  const { error } = await supabase.from('app_settings')
    .upsert({ key: SCHALTER_EPOST, value: e, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

async function rufe<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('epost-brief', { body });
  if (error) {
    let text = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') { try { const j = await ctx.json(); if (j?.error) text = String(j.error); } catch { /* leer */ } }
    throw new Error(text);
  }
  return data as T;
}

export interface Verbindung { ok: boolean; erreichbar: boolean; anmeldung: boolean; fehler: string | null; live: boolean; aktiv: boolean }
export const pruefeEpostVerbindung = () => rufe<Verbindung>({ aktion: 'verbindung' });

/** Ein Teil des Briefs (PDF oder PNG/JPG), aus der App (base64) oder aus Supabase Storage. */
export interface BriefTeil { art: 'pdf' | 'bild'; base64?: string; storage?: { bucket: string; path: string }; mime?: string; name?: string }

/** Woraus ein Brief gebaut wurde – gespeichert am Brief, damit er nach einem Fehler korrigiert neu gesendet werden kann. */
export interface BriefEntwurf {
  anschrift: string; betreff: string; text: string; mitAnschreiben: boolean;
  anlagen: { name: string; mime: string; drive?: string; storage?: { bucket: string; path: string } }[];
  optionen?: BriefOptionen;
}

export interface BriefAuftrag {
  empfaenger: Empfaenger;
  teile: BriefTeil[];            // alles in EINEM Umschlag, in dieser Reihenfolge
  /** true: Teil 1 ist ein Anschreiben auf dem Briefpapier (Anschrift im Fenster) – sonst Deckblatt der Post. */
  anschrift_im_dokument?: boolean;
  optionen?: BriefOptionen;
  betreff?: string;
  dateiname?: string;
  objekt_id?: string | null;
  /** Für den Verlauf beim Empfänger */
  empfaenger_typ?: 'mieter' | 'eigentuemer' | 'kontakt' | 'dienstleister' | null;
  empfaenger_id?: string | null;
  bezug_typ?: 'mieter' | 'eigentuemer' | 'kontakt' | 'dienstleister' | 'email' | 'dokument' | string | null;
  bezug_id?: string | null;
  entwurf?: BriefEntwurf;
  /** Korrektur: ID des fehlerhaften Briefs, den dieser ersetzt */
  ersetzt_id?: string | null;
}

export interface BriefErgebnis { test: boolean; briefe: { id: string; letter_id: number | null; pdf_base64?: string | null }[] }

/** Höchstens so viele Briefe schickt die App je Aufruf (Grenze der Edge Function). */
export const BRIEFE_JE_AUFRUF = 10;

/** pdfZurueck: fertiges Brief-PDF je Brief zurück (für die Ablage in der Akte). */
export const sendeBriefe = (briefe: BriefAuftrag[], anlagen: BriefTeil[] = [], batchId?: number, pdfZurueck = false) =>
  rufe<BriefErgebnis>({ aktion: 'senden', briefe, anlagen, batch_id: batchId, pdf_zurueck: pdfZurueck });

export const sendeBrief = (brief: BriefAuftrag, pdfZurueck = false) => sendeBriefe([brief], [], undefined, pdfZurueck);

/** Vermerkt am Brief, wo sein PDF in der Akte liegt. */
export const vermerkeAblage = (id: string, dateiId: string) => rufe<{ ok: boolean }>({ aktion: 'ablage', id, datei_id: dateiId });

/** Fertiges Brief-PDF zur Ansicht – es wird nichts verschickt. */
export const briefVorschau = (brief: BriefAuftrag, anlagen: BriefTeil[] = []) =>
  rufe<{ pdf_base64: string; deckblatt: boolean }>({ aktion: 'vorschau', briefe: [brief], anlagen });

/** Serienbrief: schickt in Paketen, meldet Fortschritt, bricht beim ersten Fehler ab (Bisheriges bleibt verschickt). */
export async function sendeSerienbrief(briefe: BriefAuftrag[], anlagen: BriefTeil[], fortschritt?: (fertig: number, gesamt: number) => void): Promise<{ batchId: number; gesendet: number }> {
  const batchId = Math.floor(Date.now() / 1000) % 2_000_000_000;
  let gesendet = 0;
  for (let i = 0; i < briefe.length; i += BRIEFE_JE_AUFRUF) {
    const paket = briefe.slice(i, i + BRIEFE_JE_AUFRUF);
    await sendeBriefe(paket, anlagen, batchId);
    gesendet += paket.length;
    fortschritt?.(gesendet, briefe.length);
  }
  return { batchId, gesendet };
}

/** Fragt den Stand offener Briefe bei der Post ab (ohne ids: alle nicht abgeschlossenen). */
export const aktualisiereBriefStatus = (ids?: string[]) => rufe<{ aktualisiert: number; abgeschlossen?: number }>({ aktion: 'status', ids });

/** Testbrief so, wie die Post ihn drucken würde (48 h nach Verarbeitung abrufbar). */
export const ladeTestbrief = (letterId: number) => rufe<{ pdf_base64: string | null; dateiname: string | null }>({ aktion: 'testergebnis', letter_id: letterId });

export interface BriefZeile {
  id: string; letter_id: number | null; test: boolean; status_id: number | null; status_details: string | null;
  fehler: { level?: string; code?: string; description?: string }[] | null; seiten: number | null;
  einschreiben: string | null; einschreiben_nr: string | null; einschreiben_status: string | null;
  betreff: string | null; empfaenger: Empfaenger; created_at: string; gedruckt_at: string | null; aktualisiert_at: string;
  ablage_datei_id: string | null;
  entwurf: BriefEntwurf | null; ersetzt_durch: string | null; bezug_typ: string | null; bezug_id: string | null; objekt_id: string | null;
}

/** Briefe an einen Empfänger, neueste zuerst. */
export async function ladeBriefeAn(typ: 'mieter' | 'eigentuemer' | 'kontakt' | 'dienstleister', id: string): Promise<BriefZeile[]> {
  const { data, error } = await supabase.from('epost_briefe')
    .select('id, letter_id, test, status_id, status_details, fehler, seiten, einschreiben, einschreiben_nr, einschreiben_status, betreff, empfaenger, created_at, gedruckt_at, aktualisiert_at, ablage_datei_id, entwurf, ersetzt_durch, bezug_typ, bezug_id, objekt_id')
    .eq('empfaenger_typ', typ).eq('empfaenger_id', id).order('created_at', { ascending: false }).limit(100);
  if (error) throw error;
  return (data ?? []) as BriefZeile[];
}
